import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { hasAtLeastRole, type Role } from '@/types/domain';
import { ErrorState } from '@/components/states';
import { ApiError } from '@/lib/apiError';
import { ErrorCode } from '@/types/api';

// -----------------------------------------------------------------------------
// Route guards.
//
// ⚠ THESE ARE UX, NOT SECURITY. Every one of them can be defeated by editing the
// URL, and that is fine - the API authorises every request independently and a
// hidden button is not a protected action. What they buy is that a staff member
// sees "you do not have permission" once, instead of a screen that renders and
// then fills with 403s.
// -----------------------------------------------------------------------------

/** Full-screen spinner while the session is being verified on first load. */
function SessionLoading(): JSX.Element {
  return (
    <div
      className="flex h-screen items-center justify-center bg-canvas"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
        <p className="text-base text-muted-foreground">Checking your access…</p>
      </div>
    </div>
  );
}

/**
 * Requires a verified staff session.
 *
 * The attempted path is carried in location state so sign-in returns the user
 * where they were going, not to the dashboard - which matters when the link they
 * followed was a deep link to a specific application.
 */
export function RequireAuth(): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'initialising') return <SessionLoading />;

  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

/**
 * Requires a role at or above `minimum`.
 *
 * Rank-based, so SUPER_ADMIN passes every ADMIN gate automatically and the
 * inheritance is expressed once rather than as a two-branch check on each route.
 */
export function RequireRole({
  minimum,
  children,
}: {
  minimum: Role;
  children?: ReactNode;
}): JSX.Element {
  const { user } = useAuth();

  if (!hasAtLeastRole(user?.role, minimum)) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <ErrorState
          error={
            new ApiError({
              code: ErrorCode.FORBIDDEN,
              message: 'Insufficient privileges for this action.',
              status: 403,
            })
          }
        />
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}

/** Bounces an already-signed-in user away from the sign-in screen. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  if (status === 'initialising') return <SessionLoading />;
  if (status === 'authenticated') return <Navigate to={from ?? '/'} replace />;

  return <>{children}</>;
}
